import { Link } from 'react-router-dom';
import { Alert, PageHeader } from '@aaimplatform/ui';
import { useSettings } from '../../hooks/useSettings';
import { SettingRowsSection, NOT_CONFIGURED } from './SettingRows';

/**
 * Notification Defaults - Platform Admin
 *
 * Read-only. Of the settings this page names, the platform settings contract
 * (PlatformSettings in platform-admin.openapi.yaml) declares one: smsEnabled,
 * which General Settings edits. The email and push channels, compliance
 * alerts, the daily digest and the per-asset-class LTV alert thresholds have
 * no declared field and no reader.
 *
 * The page used to seed an editable form from client literals -- channel
 * toggles, and a per-asset-class threshold table (Real Estate 75/85/80, and
 * so on) -- and PATCH them into the settings document. Those literals are no
 * longer shown as configuration, and the page sends nothing.
 */
export function NotificationsConfigPage() {
  const { data: settings, isLoading, error } = useSettings();

  let smsValue: string;
  if (isLoading) smsValue = 'Loading';
  else if (error) smsValue = 'Could not be read';
  else if (typeof settings?.smsEnabled !== 'boolean') smsValue = NOT_CONFIGURED;
  else smsValue = settings.smsEnabled ? 'On' : 'Off';

  return (
    <div>
      <PageHeader description="Platform-wide notification defaults for tenants: delivery channels, LTV alert thresholds, compliance alerts and the daily digest." />

      <Alert type="warning" title="Mostly not stored" className="mb-6">
        The platform settings contract declares only the SMS channel, which is
        set on General Settings. The other settings below have no declared
        field and no platform service reads them, so this page cannot show or
        save them.
      </Alert>

      {error && (
        <Alert type="error" title="Settings could not be read" className="mb-6">
          {error.message}
        </Alert>
      )}

      <SettingRowsSection
        title="Delivery Channels"
        rows={[
          { label: 'Email', description: 'Send notifications by email' },
          {
            label: 'SMS',
            description: 'Send critical alerts by SMS',
            value: (
              <span className="flex flex-col sm:items-end gap-0.5">
                <span className="text-primary">{smsValue}</span>
                <Link to="/platform-config/general" className="text-xs text-accent hover:underline">
                  Set on General Settings
                </Link>
              </span>
            ),
          },
          { label: 'Push', description: 'In-app push notifications' },
        ]}
      />

      <SettingRowsSection
        title="LTV Alert Thresholds"
        intro={
          <>
            Per-asset-class alert thresholds have no declared store. The
            contract-backed alert ladder is the Collateral Health tier set
            (green, yellow, orange, red) in each asset class policy; the Aaim
            baselines for it are on{' '}
            <Link to="/platform-config/collateral-baselines" className="text-accent hover:underline">
              Collateral Baselines
            </Link>
            .
          </>
        }
        rows={[
          {
            label: 'Per-asset-class thresholds',
            description: 'Warning and critical LTV levels for each asset class',
          },
        ]}
      />

      <SettingRowsSection
        title="Compliance Alerts"
        rows={[
          { label: 'Compliance deadline alerts', description: 'Notify when compliance deadlines approach' },
        ]}
      />

      <SettingRowsSection
        title="Daily Digest"
        rows={[
          { label: 'Daily digest', description: 'Send a daily summary of all notifications' },
          { label: 'Digest delivery time', description: 'Time to send the daily digest (UTC)' },
        ]}
      />
    </div>
  );
}
